import NetInfo, { type NetInfoState } from "@react-native-community/netinfo";
import { useEffect, useState } from "react";

// Wraps NetInfo with the small surface area screens actually need:
// `isOnline` for gating mutations, and `justReconnected` for a brief
// banner pulse when connectivity returns. NetInfo polyfills the web
// case via `navigator.onLine` + the `online`/`offline` events, so the
// hook works the same on iOS / Android / Expo web.
export interface NetworkStatus {
  isOnline: boolean;
  justReconnected: boolean;
}

const RECONNECT_PULSE_MS = 2500;

export function useNetworkStatus(): NetworkStatus {
  // Default to online — pessimistic defaults would block legitimate
  // requests during the first NetInfo dispatch (which is async on
  // web). Worse to flicker "offline" on every cold load than to miss
  // the first 50ms of an actual outage.
  const [isOnline, setIsOnline] = useState(true);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    let prevOnline = true;
    let pulseTimer: ReturnType<typeof setTimeout> | null = null;

    const apply = (state: NetInfoState): void => {
      // `isInternetReachable` is null until NetInfo has tried a probe.
      // Treat null as "assume yes" so we don't false-positive offline.
      const reachable =
        state.isInternetReachable === null
          ? true
          : state.isInternetReachable;
      const online = Boolean(state.isConnected) && reachable;
      setIsOnline(online);
      if (!prevOnline && online) {
        setJustReconnected(true);
        if (pulseTimer) clearTimeout(pulseTimer);
        pulseTimer = setTimeout(() => {
          setJustReconnected(false);
          pulseTimer = null;
        }, RECONNECT_PULSE_MS);
      }
      prevOnline = online;
    };

    // Prime once (NetInfo's listener doesn't always fire immediately).
    NetInfo.fetch().then(apply).catch(() => {
      // NetInfo can throw on locked-down web contexts (no
      // navigator.connection access). Stay optimistic.
    });
    const unsubscribe = NetInfo.addEventListener(apply);
    return () => {
      unsubscribe();
      if (pulseTimer) clearTimeout(pulseTimer);
    };
  }, []);

  return { isOnline, justReconnected };
}
